import { useState, useCallback, useRef } from 'react';

/**
 * useAlert 커스텀 훅
 * - Alert 컴포넌트의 상태 관리를 위한 공용 훅
 * - 반복되는 Alert 상태 로직을 재사용 가능하게 추상화
 * - onClose 콜백 지원으로 Alert 닫힐 때 추가 동작 실행 가능
 *
 * @returns {Object} alertState - Alert 상태 객체 (isOpen, message)
 * @returns {Function} showAlert - Alert 표시 함수 (message, onCloseCallback)
 * @returns {Function} closeAlert - Alert 닫기 함수
 *
 * @example
 * // 기본 사용
 * const { alertState, showAlert, closeAlert } = useAlert();
 * showAlert('저장되었습니다.');
 *
 * // 닫힐 때 콜백 실행
 * showAlert('저장되었습니다.', () => { refetch(); });
 *
 * <Alert isOpen={alertState.isOpen} message={alertState.message} onClose={closeAlert} />
 */
function useAlert() {
  const [alertState, setAlertState] = useState({
    isOpen: false,
    message: '',
  });

  // 닫힐 때 실행할 콜백 저장
  const onCloseCallbackRef = useRef(null);

  /**
   * Alert 표시 함수
   * @param {string} message - 표시할 메시지
   * @param {Function} onCloseCallback - Alert 닫힐 때 실행할 콜백 (선택)
   */
  const showAlert = useCallback(function (message, onCloseCallback) {
    onCloseCallbackRef.current = onCloseCallback || null;
    setAlertState({
      isOpen: true,
      message,
    });
  }, []);

  /**
   * Alert 닫기 함수
   * - 저장된 콜백이 있으면 실행
   */
  const closeAlert = useCallback(function () {
    const callback = onCloseCallbackRef.current;
    onCloseCallbackRef.current = null;
    setAlertState({
      isOpen: false,
      message: '',
    });
    if (callback) {
      callback();
    }
  }, []);

  return {
    alertState,
    showAlert,
    closeAlert,
  };
}

export default useAlert;
